import { appConfig } from '../config/appConfig';
import { apiClient } from './apiClient';

export type OsrRefineSubject = 'math' | 'yuwen' | 'english';

export type OsrRefinedShard = {
  id?: string;
  glance_surface?: import("../utils/osrPresentation").OsrGlance;
  facet?: string;
  question: string;
  answer: string;
  prompt_text?: string;
  glance_text?: string;
  glance?: { latex?: string; spoken_question?: string; type: string; preview: string; text: string; image_request?: { enabled?: boolean; prompt?: string; model?: string }; url?: string; oss_key?: string };
  screen_policy?: 'none' | 'glance';
  /** Present when source text was tagged with [particle_id=…] */
  source_particle_id?: string;
};

export type OsrRefineExtractResult = {
  ok: boolean;
  via?: string;
  provider?: string;
  model?: string;
  previousCards?: Array<{ question: string; answer: string; glanceText?: string; feedback?: string[] }>;
  extract?: 'shards' | 'topics';
  shards?: OsrRefinedShard[];
  topics?: unknown[];
  error?: string;
  message?: string;
};

export type PersistedOsrShard = OsrRefinedShard & {
  id: string;
  source_kp_id?: string;
  status?: string;
  favorited?: boolean;
  difficulty_feedback?: string;
};

export async function listPersistedOsrShards(subject: string, input: { limit?: number; cursor?: string; favorites?: boolean } = {}): Promise<PersistedOsrShard[]> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后加载口播卡');
  const query = new URLSearchParams({ subject: mapSubject(subject), limit: String(input.limit || 24) });
  if (input.cursor) query.set('cursor', input.cursor);
  if (input.favorites) query.set('favorites', 'true');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards?${query.toString()}`, {
    headers: { Accept: 'application/json', [appConfig.accessHeaderName]: `Bearer ${token}` },
    signal: AbortSignal.timeout(appConfig.defaultTimeoutMs),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('登录已失效，请重新登录后加载口播卡');
    throw new Error(`口播卡服务暂不可用（HTTP ${res.status}），请重试`);
  }
  const payload = await res.json().catch(() => null) as { items?: PersistedOsrShard[] } | null;
  if (!Array.isArray(payload?.items)) throw new Error('口播卡列表响应格式异常，请重试');
  return payload.items;
}

export async function persistOsrShard(input: {
  subject: string;
  sourceParticleId?: string;
  question: string;
  answer: string;
  promptText?: string;
  glanceText?: string;
  glanceImageUrl?: string;
  glance?: OsrRefinedShard['glance'];
}): Promise<boolean> {
  const token = await apiClient.getAccessToken();
  if (!token) return false;
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', [appConfig.accessHeaderName]: `Bearer ${token}` },
    body: JSON.stringify({
      subject: mapSubject(input.subject), source_kp_id: input.sourceParticleId,
      question: input.question, answer: input.answer, prompt_text: input.promptText,
      glance_text: input.glanceText, glance_image_url: input.glanceImageUrl,
      glance: input.glance,
    }),
  });
  return res.ok;
}

export async function generateOsrGlanceImage(input: { subject: string; prompt: string; model?: string }): Promise<OsrRefinedShard['glance'] | null> {
  const token = await apiClient.getAccessToken();
  if (!token) return null;
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/glance-image`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', [appConfig.accessHeaderName]: `Bearer ${token}` },
    body: JSON.stringify({ subject: mapSubject(input.subject), prompt: input.prompt, model: input.model }),
  });
  if (!res.ok) return null;
  const payload = await res.json().catch(() => null) as { glance?: OsrRefinedShard['glance'] } | null;
  return payload?.glance || null;
}

export async function requestPasDifficultyAdjust(input: {
  subject: string;
  particleId: string;
  question: string;
  direction: 'easier' | 'harder';
}): Promise<boolean> {
  const token = await apiClient.getAccessToken();
  if (!token) return false;
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/pas/difficulty-adjust`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', [appConfig.accessHeaderName]: `Bearer ${token}` },
    body: JSON.stringify({ subject: input.subject, particle_id: input.particleId, question: input.question, direction: input.direction }),
  });
  return res.ok;
}

function mapSubject(subject: string): OsrRefineSubject {
  if (subject === 'yuwen' || subject === 'chinese') return 'yuwen';
  if (subject === 'english') return 'english';
  return 'math';
}

/**
 * Refine oral shards via agent.kelilearning.com with AppFunctor JWT.
 * No AppFunctor proxy hop; failures surface as thrown Error / ok:false.
 */
export async function refineOsrShards(input: {
  subject: string;
  sourceText: string;
  systemHint?: string;
  model?: string;
  previousCards?: Array<{ question: string; answer: string; glanceText?: string; feedback?: string[] }>;
}): Promise<OsrRefineExtractResult> {
  const sourceText = String(input.sourceText || '').trim();
  if (sourceText.length < 12) {
    return { ok: false, error: 'source_too_short', message: '素材太短，无法生成口播卡' };
  }

  const token = await apiClient.getAccessToken();
  if (!token) {
    return { ok: false, error: 'unauthorized', message: '请先登录后再生成口播卡' };
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 120_000);
  try {
    const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/extract`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [appConfig.accessHeaderName]: `Bearer ${token}`,
      },
      body: JSON.stringify({
        subject: mapSubject(input.subject),
        extract: 'shards',
        source_text: sourceText,
        system_hint: input.systemHint || undefined,
        model: input.model?.trim() || undefined,
        previous_cards: input.previousCards,
      }),
      signal: controller.signal,
    });
    const payload = (await res.json().catch(() => null)) as OsrRefineExtractResult | null;
    if (!res.ok) {
      return {
        ok: false,
        error: payload?.error || `http_${res.status}`,
        message: payload?.message || `生成失败（${res.status}）`,
      };
    }
    if (!payload) {
      return { ok: false, error: 'empty_response', message: '生成服务返回空响应' };
    }
    return payload;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, error: 'timeout', message: '生成超时，请稍后重试' };
    }
    return {
      ok: false,
      error: 'network',
      message: error instanceof Error ? error.message : '无法连接口播精炼服务',
    };
  } finally {
    window.clearTimeout(timer);
  }
}

export async function submitOsrFeedback(shardId: string, flag: string, requestId: string, reason?: string, detail?: string): Promise<{ job_id: string | null }> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后提交反馈');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards/${encodeURIComponent(shardId)}/feedback`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ flag, request_id: requestId, reason, detail }), signal: AbortSignal.timeout(20000),
  });
  if (!res.ok) throw new Error(`反馈提交失败（HTTP ${res.status}），尚未调整序列`);
  return res.json();
}

export async function getOsrFeedbackStatus(shardId: string, jobId: string): Promise<{ status: string; error?: string }> {
  const token = await apiClient.getAccessToken();
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards/${encodeURIComponent(shardId)}/feedback/${encodeURIComponent(jobId)}`, {
    headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(20000),
  });
  if (!res.ok) throw new Error('暂时无法查询调整进度');
  return res.json();
}

export async function enqueueOsrGeneration(input: {
  subject: string; model?: string;
  groups: Array<{ request_id: string; source_particle_id: string; source_text: string; source_image?: unknown }>;
}): Promise<{ job_ids: string[] }> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后安排生成');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/generation-jobs`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...input, subject: mapSubject(input.subject) }), signal: AbortSignal.timeout(30000),
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error(payload?.message || `后台生成安排失败（HTTP ${res.status}）`);
  if (!Array.isArray(payload?.job_ids)) throw new Error('生成任务响应异常');
  return payload;
}

export async function clearOsrShards(subject: string, shardIds?: string[]): Promise<number> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后清空口播卡');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ subject: mapSubject(subject), shard_ids: shardIds }),
    signal: AbortSignal.timeout(20000),
  });
  const result = await res.json().catch(() => null);
  if (!res.ok) {
    const reasons: Record<string, string> = {
      osr_persistence_misconfigured: '口播卡服务配置异常，请联系管理员',
      osr_clear_unreachable: '暂时无法连接口播卡服务，请稍后重试',
      clear_shards_failed: '后台清空失败，请稍后重试',
      invalid_clear_scope: '清空范围异常，请刷新列表后重试',
    };
    const reason = res.status === 401 ? '登录已失效，请重新登录' : reasons[result?.error] || '清空失败，请稍后重试';
    throw new Error(`${reason}（HTTP ${res.status}），列表未清空`);
  }
  if (result?.ok !== true || typeof result.deleted_count !== 'number') throw new Error('清空响应异常，请刷新确认');
  return result.deleted_count;
}

export type OsrGenerationJob = {
  job_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  produced_count: number;
  created_at: string;
  updated_at: string;
  error: string | null;
};

export async function listOsrGenerationJobs(subject: string): Promise<OsrGenerationJob[]> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后查看生成进度');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/generation-jobs?${new URLSearchParams({ subject: mapSubject(subject) })}`, {
    headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(20000),
  });
  if (!res.ok) throw new Error(`无法查询生成进度（HTTP ${res.status}），请稍后刷新`);
  const payload = await res.json();
  if (!Array.isArray(payload?.jobs)) throw new Error('生成进度响应异常，请稍后刷新');
  return payload.jobs;
}

export async function retryOsrGenerationJob(subject: string, jobId: string): Promise<void> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后重试生成');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/generation-jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ subject: mapSubject(subject) }), signal: AbortSignal.timeout(20000),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(payload?.message || (res.status === 409 ? '任务状态已变化，请刷新进度' : `重试提交失败（HTTP ${res.status}）`));
  }
}

export async function deleteFailedOsrGenerationJobs(subject: string, jobIds: string[]): Promise<string[]> {
  const token = await apiClient.getAccessToken();
  if (!token) throw new Error('请登录后删除失败任务');
  const res = await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/generation-jobs/failed`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ subject: mapSubject(subject), job_ids: jobIds }), signal: AbortSignal.timeout(20000),
  });
  if (!res.ok) throw new Error(`删除失败任务未成功（HTTP ${res.status}），请稍后重试`);
  const payload = await res.json();
  if (!Array.isArray(payload?.deleted_ids) || payload.deleted_ids.some((id: unknown) => typeof id !== 'string')) throw new Error('删除响应异常，请刷新任务状态');
  return payload.deleted_ids;
}

export type OsrSourceStats = {source_kp_id:string;total:number;unheard:number;pending:number;suppressed:boolean;cooling:boolean};
export async function getOsrSourceStats(subject:string):Promise<OsrSourceStats[]> {
 const token=await apiClient.getAccessToken();
 const res=await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/source-stats?${new URLSearchParams({subject:mapSubject(subject)})}`,{headers:{Authorization:`Bearer ${token}`},signal:AbortSignal.timeout(20000)});
 if(!res.ok)throw new Error(res.status===404 ? '库存接口未更新，请重新发布 OSR 和 keliagent' : `库存查询失败（HTTP ${res.status}），本次未安排生成`);
 const data=await res.json();if(!Array.isArray(data.items))throw new Error('库存响应异常');return data.items;
}
export async function setOsrFavorite(id:string,favorited:boolean):Promise<void> {
 const token=await apiClient.getAccessToken();
 const res=await fetch(`${appConfig.keliagentBaseUrl}/api/v1/osr/shards/${encodeURIComponent(id)}/favorite`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},body:JSON.stringify({favorited}),signal:AbortSignal.timeout(20000)});
 if(!res.ok)throw new Error('收藏未保存，请重试');
}
